import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import type { Category, Subcategory } from '@/types/common';

interface SubcategoryModalProps {
  subcategory?: Subcategory;
  categories: Category[];
  onClose: () => void;
  onSubmit: (data: { name: string; categoryId: string; }) => Promise<void>;
}

export default function SubcategoryModal({ subcategory, categories, onClose, onSubmit }: SubcategoryModalProps) {
  const [formData, setFormData] = useState({
    name: subcategory?.name || '',
    categoryId: subcategory?.category_id || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.categoryId || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting subcategory:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg 
                bg-white dark:bg-dark-bg p-6 shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 dark:text-dark-text-primary mb-4"
                >
                  {subcategory ? 'Edit Subcategory' : 'New Subcategory'}
                </Dialog.Title>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
                    >
                      Subcategory Name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="block w-full rounded-lg border border-gray-300 dark:border-dark-border shadow-sm 
                          focus:ring-1 focus:ring-primary-500 focus:border-primary-500 
                          dark:bg-dark-bg-light dark:text-dark-text-primary
                          py-2.5 pl-3 pr-10 text-sm transition-colors duration-200"
                        placeholder="Enter subcategory name"
                      />
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <i className="fas fa-pencil-alt text-gray-400 dark:text-dark-text-secondary text-xs" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="categoryId"
                      className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
                    >
                      Parent Category
                    </label>
                    <div className="relative">
                      <select
                        id="categoryId"
                        value={formData.categoryId}
                        onChange={(e) => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
                        className="block w-full rounded-lg border border-gray-300 dark:border-dark-border shadow-sm 
                          focus:ring-1 focus:ring-primary-500 focus:border-primary-500 
                          dark:bg-dark-bg-light dark:text-dark-text-primary
                          py-2.5 pl-3 pr-10 text-sm transition-colors duration-200
                          appearance-none"
                      >
                        <option value="">Select a category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <i className="fas fa-chevron-down text-gray-400 dark:text-dark-text-secondary text-xs" />
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-primary 
                        bg-white dark:bg-dark-bg-light border border-gray-300 dark:border-dark-border rounded-lg 
                        hover:bg-gray-50 dark:hover:bg-dark-bg-lighter
                        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
                        transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!formData.name || !formData.categoryId || isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white 
                        bg-primary-600 border border-transparent rounded-lg
                        hover:bg-primary-700 focus:outline-none focus:ring-2 
                        focus:ring-offset-2 focus:ring-primary-500
                        disabled:opacity-50 disabled:cursor-not-allowed
                        transition-colors duration-200
                        inline-flex items-center"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        subcategory ? 'Save Changes' : 'Create Subcategory'
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
} 